import React, { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { DIALOG_BUTTON, FILLED_SELECT, SOFT_BUTTON } from '../../../organisations/users/organisationUserFormat';

import Button from 'CommonComponents/Button';
import CheckboxMultiSelect from 'CommonComponents/CheckboxMultiSelect';
import Modal from 'CommonComponents/Modal';
import ModalHeader from 'CommonComponents/ModalHeader';

import useModal from 'Hooks/useModal';

export const EVENT_ADMIN_ROLE = 'Event Admin';
const DEFAULT_ROLE = 'Level 1 - User';

const LABEL = 'tw-mb-0 tw-pt-2.5 tw-text-0.5sm tw-font-normal tw-normal-case tw-text-charcoal-75';
const FILLED_BOX = 'tw-rounded-lg tw-bg-charcoal-10 tw-px-3 tw-py-2';
const RADIO_LABEL = 'tw-mb-0 tw-flex tw-items-center tw-gap-x-2.5 tw-text-0.5sm tw-font-light tw-text-charcoal';

const ids = (items) => (items ?? []).map((item) => item.id);
const options = (items) => (items ?? []).map((item) => ({ value: item.id, label: item.name }));

const schema = (adding) =>
    Yup.object({
        userId: adding ? Yup.string().required($t('Choose a user')) : Yup.string().nullable(),
        roleId: Yup.string().required($t('Choose a role')),
    });

// Every centre is offered whatever departments are chosen: a user may hold a
// cost centre without its department. Each label names its department.
const centreOptions = (centres, parents, parentKey) => {
    const parentNames = new Map((parents ?? []).map((parent) => [String(parent.id), parent.name]));

    return (centres ?? []).map((centre) => {
        const parentName = parentNames.get(String(centre[parentKey]));

        return {
            value: centre.id,
            label: parentName ? $t('{{centre}} — {{department}}', { centre: centre.name, department: parentName }) : centre.name,
        };
    });
};

function initialValues(user, roles) {
    if (!user) {
        const defaultRole = roles.find((role) => role.name === DEFAULT_ROLE) ?? roles[0];

        return {
            userId: '',
            roleId: defaultRole ? String(defaultRole.id) : '',
            modulesIds: [],
            departmentsIds: [],
            venuesIds: [],
            costcentersIds: [],
            revdepartmentsIds: [],
            revcostcentersIds: [],
            enabled: true,
        };
    }

    return {
        userId: String(user.id),
        roleId: user.role ? String(user.role.id) : '',
        // An Event Admin changed to another role starts from the modules they hold now.
        modulesIds: ids(user.modules),
        departmentsIds: ids(user.departments),
        venuesIds: ids(user.venues),
        costcentersIds: ids(user.costcenters),
        revdepartmentsIds: ids(user.revdepartments),
        revcostcentersIds: ids(user.revcostcenters),
        enabled: user.is_active,
    };
}

function FieldRow({ label, htmlFor, error, children }) {
    return (
        <>
            <label htmlFor={htmlFor} className={LABEL}>
                {label}
            </label>
            <div className="tw-min-w-0 tw-space-y-1">
                {children}
                {error ? <div className="tw-text-xs tw-text-red">{error}</div> : null}
            </div>
        </>
    );
}

function RadioGroup({ name, value, choices, disabled, onChange }) {
    return (
        <div role="radiogroup" className={classNames(FILLED_BOX, 'tw-flex tw-flex-wrap tw-gap-x-3.5 tw-gap-y-2')}>
            {choices.map((choice) => (
                <label key={choice.value} className={classNames(RADIO_LABEL, (disabled || choice.disabled) && 'tw-cursor-not-allowed tw-opacity-60')}>
                    <input
                        type="radio"
                        name={name}
                        value={choice.value}
                        checked={String(value) === String(choice.value)}
                        disabled={disabled || choice.disabled}
                        onChange={() => onChange(choice.value)}
                    />
                    {choice.label}
                </label>
            ))}
        </div>
    );
}

/**
 * Add a user to the project, or edit one already on it: role, modules, places
 * and, when editing, project access. An Event Admin holds every module, so the
 * module list gives way to a read-only note for that role.
 *
 * `open` false keeps the dialog closed; `user` null adds, a row edits.
 */
export default function ProjectUserModal({
    open,
    user,
    roles,
    departments,
    costcenters,
    revDepartments,
    revCostcenters,
    venues,
    modules,
    organisationUsers,
    storeUrl,
    updateUrl,
    canAssignEventAdmin,
    canEnable,
    authId,
    onClose,
    onSaved,
}) {
    const modal = useModal();
    const { show, hide } = modal;
    const adding = !user;
    // Set when the save would leave the project without an Event Admin, until confirmed.
    const [lastAdminWarning, setLastAdminWarning] = useState(null);
    const [requestError, setRequestError] = useState(null);

    useEffect(() => {
        if (open) {
            setLastAdminWarning(null);
            setRequestError(null);
            show();
        } else {
            hide();
        }
    }, [open, show, hide]);

    // A member keeps a role the plan no longer offers while the rest of their access is edited.
    const roleList = useMemo(
        () => (user?.role && !roles.some((role) => String(role.id) === String(user.role.id)) ? [...roles, user.role] : roles),
        [roles, user],
    );
    const roleChoices = roleList.map((role) => ({
        value: String(role.id),
        label: $t(role.name),
        // Only those who may grant Event Admin may give it or take it away.
        disabled: !canAssignEventAdmin && (role.name === EVENT_ADMIN_ROLE || !!user?.is_event_admin),
    }));
    const isEventAdminRole = (roleId) => roleList.some((role) => String(role.id) === String(roleId) && role.name === EVENT_ADMIN_ROLE);

    // Mirrors the server: nobody changes their own or a super-admin's access, and an
    // Event Admin's access is changed only by those who may assign Event Admin.
    const mayChangeAccess = !adding && canEnable && String(user.id) !== String(authId) && !user.is_super_admin && (canAssignEventAdmin || !user.is_event_admin);

    const submit = (values, { setSubmitting }, confirmLastAdmin = false) => {
        setRequestError(null);

        const payload = {
            rolesIds: [Number(values.roleId)],
            modulesIds: isEventAdminRole(values.roleId) ? [] : values.modulesIds,
            departmentsIds: values.departmentsIds,
            venuesIds: values.venuesIds,
            costcentersIds: values.costcentersIds,
            revdepartmentsIds: values.revdepartmentsIds,
            revcostcentersIds: values.revcostcentersIds,
        };

        const request = adding
            ? axios.post(storeUrl, { ...payload, userId: Number(values.userId) })
            : axios.put(updateUrl.replace('user_id', user.id), {
                  ...payload,
                  ...(mayChangeAccess ? { enabled: values.enabled } : {}),
                  confirm_last_admin: confirmLastAdmin,
              });

        return request
            .then(() => {
                setLastAdminWarning(null);
                onSaved(adding);
            })
            .catch((e) => {
                const response = e?.response?.data;

                if (response?.error_code === 'LAST_EVENT_ADMIN') {
                    setLastAdminWarning(response.message);

                    return;
                }

                // The dialog stays open, so nothing chosen is lost.
                setRequestError(response?.message || response?.error || $t('Something went wrong'));
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Modal
            modal={modal}
            modalIndexClass="tw-z-[110]"
            parentContainerClasses="!tw-max-w-[712px]"
            innerContainerClasses="!tw-rounded-[14px] !tw-px-[26px] !tw-py-6"
        >
            {open && (
                <Formik initialValues={initialValues(user, roles)} validationSchema={schema(adding)} onSubmit={(values, helpers) => submit(values, helpers)}>
                    {({ values, errors, touched, isSubmitting, setFieldValue, setSubmitting, handleSubmit, validateForm, setTouched }) => {
                        const eventAdmin = isEventAdminRole(values.roleId);
                        const costcenterOptions = centreOptions(costcenters, departments, 'department_id');
                        const revCostcenterOptions = centreOptions(revCostcenters, revDepartments, 'revdepartment_id');

                        const saveAnyway = () => {
                            setSubmitting(true);
                            submit(values, { setSubmitting }, true);
                        };

                        const save = () => {
                            setTouched({ userId: true, roleId: true });
                            validateForm().then((found) => Object.keys(found).length === 0 && handleSubmit());
                        };

                        return (
                            <>
                                <ModalHeader title={adding ? $t('Add user to project') : $t('Edit user')} close={onClose} />
                                <div className="tw-grid tw-grid-cols-[150px_minmax(0,1fr)] tw-items-start tw-gap-x-3 tw-gap-y-3">
                                    <FieldRow label={$t('User')} htmlFor="project-user-user" error={touched.userId && errors.userId}>
                                        {adding ? (
                                            <select
                                                id="project-user-user"
                                                className={`select-input tw-w-full ${FILLED_SELECT}`}
                                                value={values.userId}
                                                onChange={(event) => setFieldValue('userId', event.target.value)}
                                            >
                                                <option value="">{$t('Select user')}</option>
                                                {organisationUsers.map((item) => (
                                                    <option key={item.id} value={item.id}>
                                                        {`${item.first_name} ${item.last_name}`}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <div id="project-user-user" className={classNames(FILLED_BOX, 'tw-text-sm tw-text-charcoal')}>
                                                {user.name} <span className="tw-text-charcoal-50">{user.email}</span>
                                            </div>
                                        )}
                                    </FieldRow>

                                    <FieldRow label={$t('Role')} error={touched.roleId && errors.roleId}>
                                        <RadioGroup
                                            name="project-user-role"
                                            value={values.roleId}
                                            choices={roleChoices}
                                            disabled={isSubmitting}
                                            onChange={(roleId) => setFieldValue('roleId', roleId)}
                                        />
                                    </FieldRow>

                                    <FieldRow label={$t('Modules')} htmlFor="project-user-modules">
                                        {eventAdmin ? (
                                            <p
                                                id="project-user-modules"
                                                className="tw-mb-0 tw-rounded-lg tw-bg-green-10 tw-px-3 tw-py-2 tw-text-0.5sm tw-font-light tw-text-charcoal-75"
                                            >
                                                {$t('All modules. Event Admins hold every module on the project, including any module switched on later.')}
                                            </p>
                                        ) : modules.length === 0 ? (
                                            <p id="project-user-modules" className={classNames(FILLED_BOX, 'tw-mb-0 tw-text-sm tw-text-charcoal-50')}>
                                                {$t('No modules enabled')}
                                            </p>
                                        ) : (
                                            <CheckboxMultiSelect
                                                id="project-user-modules"
                                                options={options(modules)}
                                                value={values.modulesIds}
                                                placeholder={$t('Select modules…')}
                                                searchPlaceholder={$t('Search modules')}
                                                onChange={(next) => setFieldValue('modulesIds', next)}
                                            />
                                        )}
                                    </FieldRow>

                                    <FieldRow label={$t('Departments')} htmlFor="project-user-departments">
                                        <CheckboxMultiSelect
                                            id="project-user-departments"
                                            options={options(departments)}
                                            value={values.departmentsIds}
                                            placeholder={$t('Search departments…')}
                                            searchPlaceholder={$t('Search departments')}
                                            onChange={(next) => setFieldValue('departmentsIds', next)}
                                        />
                                    </FieldRow>

                                    <FieldRow label={$t('Venues')} htmlFor="project-user-venues">
                                        <CheckboxMultiSelect
                                            id="project-user-venues"
                                            options={options(venues)}
                                            value={values.venuesIds}
                                            placeholder={$t('Search venues…')}
                                            searchPlaceholder={$t('Search venues')}
                                            onChange={(next) => setFieldValue('venuesIds', next)}
                                        />
                                    </FieldRow>

                                    <FieldRow label={$t('Cost centres')} htmlFor="project-user-costcentres">
                                        <CheckboxMultiSelect
                                            id="project-user-costcentres"
                                            options={costcenterOptions}
                                            value={values.costcentersIds}
                                            placeholder={values.costcentersIds.length === 0 ? $t('Select cost centres…') : null}
                                            searchPlaceholder={$t('Search cost centres')}
                                            onChange={(next) => setFieldValue('costcentersIds', next)}
                                        />
                                    </FieldRow>

                                    <FieldRow label={$t('Rev departments')} htmlFor="project-user-revdepartments">
                                        <CheckboxMultiSelect
                                            id="project-user-revdepartments"
                                            options={options(revDepartments)}
                                            value={values.revdepartmentsIds}
                                            placeholder={$t('Search revenue departments…')}
                                            searchPlaceholder={$t('Search revenue departments')}
                                            onChange={(next) => setFieldValue('revdepartmentsIds', next)}
                                        />
                                    </FieldRow>

                                    <FieldRow label={$t('Rev cost centres')} htmlFor="project-user-revcostcentres">
                                        <CheckboxMultiSelect
                                            id="project-user-revcostcentres"
                                            options={revCostcenterOptions}
                                            value={values.revcostcentersIds}
                                            placeholder={values.revcostcentersIds.length === 0 ? $t('Select revenue cost centres…') : null}
                                            searchPlaceholder={$t('Search revenue cost centres')}
                                            onChange={(next) => setFieldValue('revcostcentersIds', next)}
                                        />
                                    </FieldRow>

                                    {!adding && (
                                        <FieldRow label={$t('Project access')}>
                                            <RadioGroup
                                                name="project-user-access"
                                                value={values.enabled ? 'active' : 'disabled'}
                                                choices={[
                                                    { value: 'active', label: $t('Active') },
                                                    { value: 'disabled', label: $t('Disabled') },
                                                ]}
                                                disabled={isSubmitting || !mayChangeAccess}
                                                onChange={(access) => setFieldValue('enabled', access === 'active')}
                                            />
                                        </FieldRow>
                                    )}
                                </div>

                                {lastAdminWarning && (
                                    <p className="tw-mb-0 tw-mt-4 tw-rounded-lg tw-border-s-4 tw-border-amber-500 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-0.5sm tw-text-charcoal">
                                        {lastAdminWarning} {$t('The project will have no Event Admin after this change.')}
                                    </p>
                                )}
                                {requestError && <p className="tw-mb-0 tw-mt-4 tw-text-0.5sm tw-font-medium tw-text-red">{requestError}</p>}

                                <div className="modal__footer tw-mt-6 tw-gap-x-2.5 !tw-space-x-0">
                                    <Button variant="secondary" className={SOFT_BUTTON} onClick={onClose} disabled={isSubmitting}>
                                        {$t('Cancel')}
                                    </Button>
                                    {lastAdminWarning ? (
                                        <Button variant="danger" className={DIALOG_BUTTON} onClick={saveAnyway} disabled={isSubmitting} loading={isSubmitting}>
                                            {$t('Save anyway')}
                                        </Button>
                                    ) : (
                                        <Button variant="primary" className={DIALOG_BUTTON} onClick={save} disabled={isSubmitting} loading={isSubmitting}>
                                            {adding ? $t('Add user') : $t('Save')}
                                        </Button>
                                    )}
                                </div>
                            </>
                        );
                    }}
                </Formik>
            )}
        </Modal>
    );
}
